import type {
  RequiredUiBlockFamilyEvidenceSource,
  RequiredUiBlockFamilyEvidenceSourcesV1,
} from "./required-ui-block-family-live-swap"

export type CertificationViewport = "desktop" | "tablet" | "mobile"
export type CertificationScheme = "light" | "dark"
export type CertificationReleaseId =
  | "baseline.v1"
  | "editorial.v1"
  | "compact.v1"

export type RequiredUiBlockFamilyEvidenceSourceSet =
  | RequiredUiBlockFamilyEvidenceSourcesV1
  | readonly RequiredUiBlockFamilyEvidenceSourcesV1[]

export interface RequiredUiBlockCertificationCondition {
  conditionId: string
  releaseId: CertificationReleaseId
  scheme: CertificationScheme
  viewport: CertificationViewport
  width: number
  height: number
}

export interface RequiredUiBlockArtifactCertificationPlan {
  artifactRef: string
  artifactHash: `sha256:${string}`
  conditions: RequiredUiBlockCertificationCondition[]
}

export interface RequiredUiBlockFamilyCertificationFamilyPlan {
  patternId: string
  familyId: string
  variantId: string
  sourceId: string
  sourceContentHash: `sha256:${string}`
  canonicalArtifacts: RequiredUiBlockArtifactCertificationPlan[]
}

export interface RequiredUiBlockFamilyCertificationPlan {
  schemaVersion: "cogo.required-ui-block-family-certification-plan.v1"
  executionMode: "complete" | "partial"
  sourceContentHashes: Array<`sha256:${string}`>
  families: RequiredUiBlockFamilyCertificationFamilyPlan[]
  expectedFamilyCount: number
  expectedArtifactCount: number
  expectedConditionCountPerArtifact: number
  expectedObservationCount: number
  blockers: string[]
}

export interface RequiredUiBlockFamilyCertificationFilters {
  patterns?: readonly string[]
  artifacts?: readonly string[]
  conditions?: readonly string[]
}

export const REQUIRED_UI_BLOCK_CERTIFICATION_VIEWPORTS = Object.freeze({
  desktop: Object.freeze({ width: 1280, height: 800 }),
  tablet: Object.freeze({ width: 768, height: 1024 }),
  mobile: Object.freeze({ width: 375, height: 667 }),
} satisfies Record<CertificationViewport, { width: number; height: number }>)

export const REQUIRED_UI_BLOCK_CERTIFICATION_RELEASES = Object.freeze([
  "baseline.v1",
  "editorial.v1",
  "compact.v1",
] as const)

export const REQUIRED_UI_BLOCK_CERTIFICATION_SCHEMES = Object.freeze([
  "light",
  "dark",
] as const)

export const REQUIRED_UI_BLOCK_CERTIFICATION_VIEWPORT_ORDER = Object.freeze([
  "desktop",
  "tablet",
  "mobile",
] as const)

const SHA256 = /^sha256:[a-f0-9]{64}$/u
const EXPECTED_FAMILY_COUNT = 26
const EXPECTED_ARTIFACT_COUNT = 52
const EXPECTED_CONDITION_COUNT = 18
const EXPECTED_OBSERVATION_COUNT = 936

function stableUnique(values: readonly string[]): string[] {
  return [...new Set(values)]
}

function appendBlocker(blockers: string[], blocker: string): void {
  if (!blockers.includes(blocker)) blockers.push(blocker)
}

function conditionTemplate(
  patternId: string,
  artifactRef: string,
): RequiredUiBlockCertificationCondition[] {
  const conditions: RequiredUiBlockCertificationCondition[] = []
  for (const releaseId of REQUIRED_UI_BLOCK_CERTIFICATION_RELEASES) {
    for (const scheme of REQUIRED_UI_BLOCK_CERTIFICATION_SCHEMES) {
      for (const viewport of REQUIRED_UI_BLOCK_CERTIFICATION_VIEWPORT_ORDER) {
        const size = REQUIRED_UI_BLOCK_CERTIFICATION_VIEWPORTS[viewport]
        conditions.push({
          conditionId: `${patternId}:${artifactRef}:${releaseId}:${scheme}:${viewport}`,
          releaseId,
          scheme,
          viewport,
          width: size.width,
          height: size.height,
        })
      }
    }
  }
  return conditions
}

function selected(
  value: string,
  filter: ReadonlySet<string> | undefined,
): boolean {
  return !filter || filter.has(value)
}

function validateReleaseSet(
  source: RequiredUiBlockFamilyEvidenceSourcesV1,
  blockers: string[],
): void {
  const releaseIds = source.releaseSet.map((release) => release.releaseId)
  if (
    JSON.stringify(releaseIds) !==
      JSON.stringify(REQUIRED_UI_BLOCK_CERTIFICATION_RELEASES)
  ) {
    appendBlocker(blockers, `release-set-mismatch:${source.sourceId}`)
  }
  if (
    source.releaseSet.some(
      (release) => !SHA256.test(release.contentHash),
    )
  ) {
    appendBlocker(blockers, `release-hash-malformed:${source.sourceId}`)
  }
  if (new Set(source.releaseSet.map((release) => release.contentHash)).size !== 3) {
    appendBlocker(blockers, `release-hash-not-distinct:${source.sourceId}`)
  }
}

function validateFamily(
  family: RequiredUiBlockFamilyEvidenceSource,
  source: RequiredUiBlockFamilyEvidenceSourcesV1,
  blockers: string[],
): void {
  if (family.canonicalArtifacts.length !== 2) {
    appendBlocker(blockers, `canonical-artifact-count:${family.patternId}`)
  }
  const refs = family.canonicalArtifacts.map((artifact) => artifact.artifactRef)
  if (new Set(refs).size !== refs.length) {
    appendBlocker(blockers, `canonical-artifact-duplicate:${family.patternId}`)
  }
  for (const artifact of family.canonicalArtifacts) {
    if (!SHA256.test(artifact.artifactHash)) {
      appendBlocker(
        blockers,
        `canonical-artifact-hash-malformed:${family.patternId}:${artifact.artifactRef}`,
      )
    }
  }
  if (!SHA256.test(source.contentHash)) {
    appendBlocker(blockers, `source-content-hash-malformed:${source.sourceId}`)
  }
}

export function buildRequiredUiBlockFamilyCertificationPlan(
  sourceSet: RequiredUiBlockFamilyEvidenceSourceSet,
  filters: RequiredUiBlockFamilyCertificationFilters = {},
): RequiredUiBlockFamilyCertificationPlan {
  const sources: RequiredUiBlockFamilyEvidenceSourcesV1[] =
    "schemaVersion" in sourceSet ? [sourceSet] : [...sourceSet]
  const blockers: string[] = []
  const patternFilter = filters.patterns
    ? new Set(stableUnique(filters.patterns))
    : undefined
  const artifactFilter = filters.artifacts
    ? new Set(stableUnique(filters.artifacts))
    : undefined
  const conditionFilter = filters.conditions
    ? new Set(stableUnique(filters.conditions))
    : undefined
  const executionMode = patternFilter || artifactFilter || conditionFilter
    ? "partial"
    : "complete"
  if (executionMode === "partial") appendBlocker(blockers, "partial-run")

  const sourceIds = sources.map((source) => source.sourceId)
  if (new Set(sourceIds).size !== sourceIds.length) {
    appendBlocker(blockers, "source-id-duplicate")
  }

  const familyEntries = sources.flatMap((source) => {
    validateReleaseSet(source, blockers)
    return source.families.map((family) => ({ source, family }))
  })
  const patternIds = familyEntries.map(({ family }) => family.patternId)
  if (new Set(patternIds).size !== patternIds.length) {
    for (const patternId of patternIds) {
      if (patternIds.indexOf(patternId) !== patternIds.lastIndexOf(patternId)) {
        appendBlocker(blockers, `pattern-duplicate:${patternId}`)
      }
    }
  }
  if (familyEntries.length !== EXPECTED_FAMILY_COUNT) {
    appendBlocker(blockers, `family-count:${familyEntries.length}/${EXPECTED_FAMILY_COUNT}`)
  }

  const families = familyEntries.flatMap(({ source, family }) => {
    validateFamily(family, source, blockers)
    if (!selected(family.patternId, patternFilter)) return []
    const canonicalArtifacts = family.canonicalArtifacts.flatMap((artifact) => {
      if (!selected(artifact.artifactRef, artifactFilter)) return []
      const conditions = conditionTemplate(
        family.patternId,
        artifact.artifactRef,
      ).filter((condition) => selected(condition.conditionId, conditionFilter))
      return [{
        artifactRef: artifact.artifactRef,
        artifactHash: artifact.artifactHash,
        conditions,
      }]
    })
    return [{
      patternId: family.patternId,
      familyId: family.familyId,
      variantId: family.variantId,
      sourceId: source.sourceId,
      sourceContentHash: source.contentHash,
      canonicalArtifacts,
    }]
  })

  const artifactCount = familyEntries.reduce(
    (count, { family }) => count + family.canonicalArtifacts.length,
    0,
  )
  if (artifactCount !== EXPECTED_ARTIFACT_COUNT) {
    appendBlocker(blockers, `artifact-count:${artifactCount}/${EXPECTED_ARTIFACT_COUNT}`)
  }

  const allConditions = families.flatMap((family) =>
    family.canonicalArtifacts.flatMap((artifact) => artifact.conditions),
  )
  if (new Set(allConditions.map((condition) => condition.conditionId)).size !== allConditions.length) {
    appendBlocker(blockers, "condition-id-duplicate")
  }

  if (executionMode === "complete") {
    if (
      families.some((family) =>
        family.canonicalArtifacts.some(
          (artifact) => artifact.conditions.length !== EXPECTED_CONDITION_COUNT,
        ),
      )
    ) {
      appendBlocker(blockers, "condition-count-per-artifact-mismatch")
    }
    if (allConditions.length !== EXPECTED_OBSERVATION_COUNT) {
      appendBlocker(
        blockers,
        `observation-count:${allConditions.length}/${EXPECTED_OBSERVATION_COUNT}`,
      )
    }
  }

  return {
    schemaVersion: "cogo.required-ui-block-family-certification-plan.v1",
    executionMode,
    sourceContentHashes: sources.map((source) => source.contentHash),
    families,
    expectedFamilyCount: EXPECTED_FAMILY_COUNT,
    expectedArtifactCount: EXPECTED_ARTIFACT_COUNT,
    expectedConditionCountPerArtifact: EXPECTED_CONDITION_COUNT,
    expectedObservationCount: EXPECTED_OBSERVATION_COUNT,
    blockers: blockers.sort(),
  }
}
